import {ChangeDetectorRef, Component, OnInit, ViewChild, inject} from '@angular/core';
import {ActivatedRoute, Router, RouterLink} from '@angular/router';
import {I18nService} from '../../../../_services/gui/i18n.service';
import {CompaniesService} from '../../../../_services/data/companies.service';
import {Company} from '../../../../_data/admin/company';
import {AbstractControl, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, ValidationErrors, Validators, ReactiveFormsModule} from '@angular/forms';
import {MatStep, MatStepper, MatStepLabel, MatStepperPrevious} from '@angular/material/stepper';
import {BranchesService} from '../../../../_services/data/branches.service';
import {AddBranch} from '../../../../_data/admin/add-branch';
import {HttpResponse} from '@angular/common/http';
import {MemberInfo} from '../../../../_data/admin/member-info';
import {Observable} from 'rxjs';
import {Branch} from '../../../../_data/admin/branch';
import {EditBranch} from '../../../../_data/admin/edit-branch';
import {stringify} from 'flatted';
import {NotificationService} from '../../../../_services/gui/notification.service';
import {isCompanyBranch} from '../../../../_utils/type-guards';
import {filter, map, tap} from 'rxjs/operators';
import {exists, ofOr} from '../../../../_utils/operators';
import {getStateFromRouter} from '../../../../_utils/nav';
import {startsWithIgnoreCase} from '../../../../_utils/lang';
import * as _ from 'lodash';
import {NgClass, KeyValuePipe} from '@angular/common';
import {ValidationErrorsComponent} from '../../../../common/forms/validation-errors/validation-errors.component';
import {TranslateModule} from '@ngx-translate/core';

@Component({
    selector: 'app-create-branch',
    templateUrl: './create-branch.component.html',
    styleUrls: ['./create-branch.component.scss'],
    imports: [MatStepper, MatStep, ReactiveFormsModule, NgClass, MatStepLabel, ValidationErrorsComponent, RouterLink, MatStepperPrevious, KeyValuePipe, TranslateModule]
})
export class CreateBranchComponent implements OnInit {

  isEditingBranch: boolean = false;
  company: Company;
  allProducts: Map<string, string>;
  memberCodeFormSubmitted: boolean = false;
  memberCodeDetailsFormSubmitted: boolean = false;
  enteredMemberCode: string;
  companyNameParam: string;
  branchNameParam: string;
  companyFailedToLoad: boolean = false;
  branchFailedToLoad: boolean = false;

  @ViewChild('stepper') protected _stepper: MatStepper;
  @ViewChild('step1') protected _step1: MatStep;

  protected route = inject(ActivatedRoute);
  protected router = inject(Router);
  protected i18nService = inject(I18nService);
  protected branchesService = inject(BranchesService);

  private formBuilder = inject(UntypedFormBuilder);
  private notificationService = inject(NotificationService);
  private companiesService = inject(CompaniesService);
  private cdRef = inject(ChangeDetectorRef);

  private editingBranch: Branch;
  private memberCodeForm: UntypedFormGroup;
  private memberCodeDetailsForm: UntypedFormGroup;
  private selectedMemberInfo: MemberInfo;

  constructor() {

    this.i18nService.setTitle('title.admin.branches.create');

    this.companyNameParam = this.route.snapshot.params.companyName;

    //If Editing
    this.route.data.pipe(exists(), filter(value => !!value.editing)).subscribe(() => {
      this.isEditingBranch = true;

      this.branchNameParam = this.route.snapshot.params.branchId;

      console.info(`Loading branch details for ${this.companyNameParam}/${this.branchNameParam}`);
      ofOr(
        getStateFromRouter(this.router)?.branch,
        this.branchesService.getBranch(this.companyNameParam, this.branchNameParam).pipe(tap(branch => this.branchFailedToLoad = !branch))
      ).subscribe(b => {
        this.editingBranch = b;
        if(this.editingBranch) {
          console.debug(`Branch loaded: ${JSON.stringify(this.editingBranch)}`);
        }
        this.loadFormBranchDetails(b.memberCode, null, b.branchName, b.product, b.autoRef);
      });
    });

    //Load company
    ofOr(
      getStateFromRouter(this.router),
      this.companiesService.searchForCompany(this.companyNameParam).pipe(
        map(companies => {
          if(companies) {
            return companies?.find(c => c.companyName === this.companyNameParam);
          } else {
            this.companyFailedToLoad = true;
            return null;
          }
        })
      )
    ).subscribe(
      (v: Company | {company: Company; branch: Branch}) => {
        //The state from the router could have an already loaded branch object passed in.
        //If the state's null, and the getCompany method is used to pull the company (like on page refresh), then the object will just be a Company object

        console.debug(JSON.stringify(v));

        const companyBranch: {company: Company; branch: Branch} = isCompanyBranch(v) ? v : {company: v, branch: null};
        // console.debug(`CompanyBranch: ${JSON.stringify(companyBranch)}`);

        this.company = companyBranch.company;
        // console.debug(`Company loaded: ${JSON.stringify(this.company)}`);

      });

  }

  private moveToStep2() {
    if(this._step1 && this._stepper) {
      this._step1.completed = true;
      this._stepper.next();
      this.cdRef.detectChanges();
    }
  }

  private setMemberCodeErrors(errors: ValidationErrors) {
    this.memberCodeForm.controls.memberCode.setErrors(errors);
    this.memberCodeForm.controls.memberCode.markAsTouched();
    this.memberCodeForm.controls.memberCode.markAsDirty();
  }

  ngOnInit() {
    this.memberCodeForm = this.formBuilder.group({
      memberCode: new UntypedFormControl({value: '', disabled: this.isEditingBranch}, Validators.required)
    });

    this.memberCodeDetailsForm = this.formBuilder.group({
      branchName:       ['', Validators.required],
      product:          ['', Validators.required],
      refAutoGenerated: ''
    });
  }

  submitMemberCode(value) {
    console.debug('Submitting memberCode');
    this.memberCodeFormSubmitted = true;
    if(this.memberCodeForm.valid || this.isEditingBranch) {
      this._step1.completed = true;
      this.enteredMemberCode = value.memberCode;
      this.branchesService.getMemberCodeInfo(value.memberCode).subscribe((memberInfo: MemberInfo) => this.loadFormBranchDetails(value.memberCode, memberInfo));
    } else {
      console.debug(`MemberCodeForm is invalid: ${stringify(this.memberCodeForm)}`);
    }
  }

  submitMemberCodeDetails(value: any): void {
    console.debug('Submitting branch');
    // console.debug(`Company Details: ${JSON.stringify(this.company)}`);
    this.memberCodeDetailsFormSubmitted = true;
    if(this.memberCodeDetailsForm.valid) {

      console.debug(`Form value: ${stringify(value)}`);

      const companyName = this.company.companyName;

      const addBranch: AddBranch = {
        autoRef:     value.refAutoGenerated,
        branchName:  value.branchName,
        companyName: companyName,
        ifsOptions:  '',
        memberCode:  this.enteredMemberCode,
        product:     value.product
      };

      let observable: Observable<HttpResponse<string>> = this.branchesService.addBranch(addBranch);
      if(this.isEditingBranch) {
        const editBranch = addBranch as EditBranch;
        editBranch.branchName = this.editingBranch.branchName;
        editBranch.newBranchName = value.branchName;
        observable = this.branchesService.editBranch(editBranch);
      }

      observable.pipe(exists()).subscribe((r: HttpResponse<string>) => {
        if(r.status === 200 || r.status === 201) { //successful edit or add
          this.router.navigateByUrl(this.router.createUrlTree(['/admin/companies', companyName, 'details']));
        }
      });

    }
  }

  public loadFormBranchDetails(memberCode: string, memberCodeInfo?: MemberInfo, branchName?: string, product: string = '', refAutoGenerated: 'true' | 'false' = 'true') {

    const controls = this.memberCodeDetailsForm.controls;

    this.enteredMemberCode = memberCode;
    this.memberCodeForm.controls.memberCode.setValue(memberCode);

    console.debug(`Loading products for ${memberCode}...`);
    ofOr(memberCodeInfo, this.branchesService.getMemberCodeInfo(memberCode)).pipe(
      tap(m => {
        if(!m) {
          this.setMemberCodeErrors({'non-existent': true});
        }
      }),
      exists(),
      tap((m: MemberInfo) => {
        this.selectedMemberInfo = m;
        controls.branchName.setValue(branchName ?? m.si.name);
        controls.product.setValue(product ?? '');
        controls.refAutoGenerated.setValue(refAutoGenerated === 'true');
      }),
      map(m => {
        let mappedIfs = m.si.productInfos.map(
          v => (startsWithIgnoreCase(v.productName, 'IFS -') ? ['IFS', 'IFS'] : [v.product, `${v.product} - ${v.productName}`]) as [string, string]
        );
        mappedIfs = _.uniqWith(mappedIfs, _.isEqual);
        return mappedIfs;
      })
    ).subscribe((productCodes: [string, string][]) => {
      console.debug('Branch product codes', productCodes);
      this.allProducts = new Map(productCodes);
    });

    this.moveToStep2();

  }

  @ViewChild('step1')
  public set step(step: MatStep) {
    this._step1 = step;
    if(this.isEditingBranch) {
      this.moveToStep2();
    }
  }

  @ViewChild('stepper')
  public set stepper(stepper: MatStepper) {
    this._stepper = stepper;
    if(this.isEditingBranch) {
      this.moveToStep2();
    }
  }

  public get f1(): UntypedFormGroup {
    return this.memberCodeForm;
  }

  public get c1(): {[p: string]: AbstractControl} {
    return this.memberCodeForm.controls;
  }

  public get f2(): UntypedFormGroup {
    return this.memberCodeDetailsForm;
  }

  public get c2(): {[p: string]: AbstractControl} {
    return this.memberCodeDetailsForm.controls;
  }
}
